/**
 * A Versant login's pause causes and what the clinic is told — the one
 * definition the robot, the inbox, the setup callables and the panels share
 * (openspec/changes/versant-eligibility-lane, design D2).
 *
 * A Versant login is one address on a practice's codes domain, which is also
 * its Versant username. Only the practice knows the password, so a rejected
 * password is the admin's to fix; every other way a code login fails is ours.
 *
 * Pure: no Firestore and no firebase-functions, because the scrape worker
 * loads it.
 */
import * as portal from "./portalLogin.js";
import {
  ageMs, CODE_ALERT_COUNT, CODE_WINDOW_MS, type LoginPause, type LoginPolicy,
  type OpenRequest, type PauseOwner,
} from "./portalLogin.js";

/** What the clinic is told when its admin has the next step. */
export const VERSANT_SETUP_NEEDED_MESSAGE = "Versant setup needed";
/** What the clinic is told when the pause is ours to fix. */
export const VERSANT_VITALREV_FIXING_MESSAGE = "VitalRev is fixing Versant checks";

/** The practice's to fix, lifted by saving the password again: Versant
 *  refused the password, or knows no Versant user by the login's address
 *  (the user was made with another one). */
export const VERSANT_ADMIN_PAUSE_CAUSES = ["password_rejected", "username_unknown"] as const;
/** A fault on our side: a staff restart lifts these. */
export const VERSANT_VITALREV_PAUSE_CAUSES = [
  "no_email", "email_without_code", "code_rejected", "run_ended",
] as const;

export type VersantAdminPauseCause = typeof VERSANT_ADMIN_PAUSE_CAUSES[number];
export type VersantVitalrevPauseCause = typeof VERSANT_VITALREV_PAUSE_CAUSES[number];
export type VersantPauseCause = VersantAdminPauseCause | VersantVitalrevPauseCause;

export function isVersantPauseCause(value: unknown): value is VersantPauseCause {
  return (VERSANT_ADMIN_PAUSE_CAUSES as readonly unknown[]).includes(value) ||
    (VERSANT_VITALREV_PAUSE_CAUSES as readonly unknown[]).includes(value);
}

/** Whose next step a pause for `cause` is. */
export function versantPauseOwnerOf(cause: VersantPauseCause): "admin" | "vitalrev" {
  return (VERSANT_ADMIN_PAUSE_CAUSES as readonly string[]).includes(cause) ? "admin" : "vitalrev";
}

/** What the clinic is told about a pause for `cause`. */
export function versantMessageFor(cause: VersantPauseCause): string {
  return versantPauseOwnerOf(cause) === "admin"
    ? VERSANT_SETUP_NEEDED_MESSAGE
    : VERSANT_VITALREV_FIXING_MESSAGE;
}

/** Versant's causes and words, for the shared transitions. */
export const VERSANT_POLICY: LoginPolicy<VersantPauseCause> = {
  adminCauses: VERSANT_ADMIN_PAUSE_CAUSES,
  vitalrevCauses: VERSANT_VITALREV_PAUSE_CAUSES,
  setupNeededMessage: VERSANT_SETUP_NEEDED_MESSAGE,
  vitalrevFixingMessage: VERSANT_VITALREV_FIXING_MESSAGE,
};

export type VersantLoginPause = LoginPause<VersantPauseCause>;

export interface VersantLoginDoc {
  /** The login's address on the practice's codes domain — its Versant
   *  username. Made with the doc; never changes. */
  address: string;
  /** The practice that made the address; only its clinics reuse the login. */
  ownerOrgId: string;
  /** The first time a code reached the address. A record only: it decides
   *  nothing about the login's state. */
  setUpAt: string | null;
  /** When the login's password was last saved; the login is set up once it is. */
  passwordSavedAt: string | null;
  /** The login's Secret Manager secret, once its password is saved. */
  secretName: string | null;
  pause: VersantLoginPause | null;
  openRequest: OpenRequest | null;
  /** The code arrivals of the last seven days. */
  recentCodeAt: string[];
  codeAlertAt: string | null;
  createdAt: string;
}

/** The fields a transition changes, written with `update`. */
export type VersantLoginPatch = Partial<Pick<
  VersantLoginDoc,
  "setUpAt" | "passwordSavedAt" | "secretName" | "pause" | "openRequest" |
  "recentCodeAt" | "codeAlertAt"
>>;

export type VersantLoginState = "not_set_up" | "set_up" | "paused_admin" | "paused_vitalrev";

export type VersantLoginReadiness =
  | {ready: true; state: "not_set_up" | "set_up"}
  | {
    ready: false;
    state: "paused_admin" | "paused_vitalrev";
    owner: PauseOwner;
    message: string;
  };

/** A login doc as first written: its address made, no password. */
export function newVersantLogin(input: {
  address: string; ownerOrgId: string; now: Date;
}): VersantLoginDoc {
  const nowIso = input.now.toISOString();
  return {
    address: input.address.trim().toLowerCase(),
    ownerOrgId: input.ownerOrgId,
    setUpAt: null,
    passwordSavedAt: null,
    secretName: null,
    pause: null,
    openRequest: null,
    recentCodeAt: [],
    codeAlertAt: null,
    createdAt: nowIso,
  };
}

/** A login's state: a pause wins; else set up once its password is saved. */
export function loginState(doc: VersantLoginDoc | null): VersantLoginState {
  if (!doc) return "not_set_up";
  if (doc.pause?.owner === "admin") return "paused_admin";
  if (doc.pause) return "paused_vitalrev";
  return doc.passwordSavedAt ? "set_up" : "not_set_up";
}

/** Whether the robot may run a check of this login: ready unless paused. A
 *  login with no password never reaches the robot — its clinics' records
 *  carry no `secretName`. */
export function loginReadiness(doc: VersantLoginDoc | null): VersantLoginReadiness {
  const state = loginState(doc);
  switch (state) {
    case "not_set_up":
    case "set_up":
      return {ready: true, state};
    case "paused_admin":
      return {ready: false, state, owner: "admin", message: VERSANT_SETUP_NEEDED_MESSAGE};
    case "paused_vitalrev":
      return {ready: false, state, owner: "vitalrev", message: VERSANT_VITALREV_FIXING_MESSAGE};
  }
}

/**
 * A code reached the login's address. It records `setUpAt` the first time
 * and counts toward the alert: the third inside seven days stamps
 * `codeAlertAt`, unless one was stamped in the last seven. A code is shown
 * nowhere in the app and lifts no pause — a password save lifts the admin's,
 * a staff restart VitalRev's.
 */
export function onCodeArrived(
  doc: VersantLoginDoc,
  input: {now: Date},
): VersantLoginPatch {
  const {now} = input;
  const nowIso = now.toISOString();
  const recentCodeAt = (doc.recentCodeAt ?? [])
    .filter((at) => ageMs(at, now) < CODE_WINDOW_MS)
    .concat(nowIso);
  const patch: VersantLoginPatch = {recentCodeAt};
  if (recentCodeAt.length >= CODE_ALERT_COUNT && !(ageMs(doc.codeAlertAt, now) < CODE_WINDOW_MS)) {
    patch.codeAlertAt = nowIso;
  }
  if (!doc.setUpAt) patch.setUpAt = nowIso;
  return patch;
}

/** The login's password was saved: it is set up, and an admin's pause — a
 *  rejected password — is lifted. */
export function onPasswordSaved(
  doc: VersantLoginDoc,
  input: {secretName: string; now: Date},
): VersantLoginPatch {
  return {
    passwordSavedAt: input.now.toISOString(),
    secretName: input.secretName,
    ...(doc.pause?.owner === "admin" ? {pause: null} : {}),
  };
}

/** Pause the login for `cause`. Null when already paused: the first writer wins. */
export function pauseLogin(
  doc: VersantLoginDoc,
  input: {cause: VersantPauseCause; requestId: string | null; checkId: string | null; now: Date},
): VersantLoginPatch | null {
  return portal.pauseLogin(VERSANT_POLICY, doc, input);
}

/** A clinic's Versant record, `integrations.versant` on its doc: the login it
 *  uses, and `secretName` exactly while that login's password is saved — so
 *  `integrationConfigured` and `laneGate` read it as they read VSP's and
 *  EyeMed's. Never a password. */
export interface VersantClinicRecord {
  loginId: string;
  /** The login's address — its Versant username. */
  username: string;
  secretName?: string;
  configuredAt: string;
  configuredBy: string;
}

/** The login a clinic's Versant record names, or null for no record. */
export function versantRecordLoginId(record: unknown): string | null {
  const loginId = (record as {loginId?: unknown} | null | undefined)?.loginId;
  return typeof loginId === "string" && loginId ? loginId : null;
}

/** A staff restart: only the VitalRev pause staff saw. */
export function restartLogin(
  doc: VersantLoginDoc | null,
  input: {pausedAt: string},
): VersantLoginPatch | null {
  return portal.restartLogin(doc, input);
}

/** The robot met the code page: the request is open until it ends. */
export function openCodeRequest(input: {id: string; checkId: string; now: Date}): VersantLoginPatch {
  return portal.openCodeRequest(input);
}

/** The request completed. Null when it is no longer the open one. */
export function completeCodeRequest(doc: VersantLoginDoc, requestId: string): VersantLoginPatch | null {
  return portal.completeCodeRequest(doc, requestId);
}

/** An open request left by a run that died (portalLogin.ts `isDeadRequest`). */
export function isDeadRequest(open: OpenRequest | null, input: {checkId: string; now: Date}): boolean {
  return portal.isDeadRequest(open, input);
}
